import { useEffect, useRef, useState } from 'react'
import { Bell, X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import clsx from 'clsx'
import { useNotificaciones } from '../hooks/useNotificaciones'
import type { NotificacionInterna } from '../services/api'

/**
 * Campana de notificaciones internas en el header del portal admin.
 * Visible solo si el usuario tiene algún rol interno (lo gobierna el hook).
 * El backend entrega únicamente lo visible para la audiencia del rol.
 */
export default function NotificacionesBell({ variant = 'default' }: { variant?: 'default' | 'onDark' } = {}) {
  const { items, unread, habilitado, marcar, marcarTodas } = useNotificaciones()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  if (!habilitado) return null

  const onItem = (n: NotificacionInterna) => {
    marcar(n)
    setOpen(false)
    if (n.link) navigate(n.link)
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        title="Notificaciones"
        className={clsx(
          'relative flex h-9 w-9 items-center justify-center rounded-lg transition',
          variant === 'onDark'
            ? 'text-surface-300 hover:bg-white/10 hover:text-white'
            : 'border border-surface-200 bg-white text-surface-600 hover:text-surface-900 dark:border-surface-700 dark:bg-surface-900 dark:text-surface-300 dark:hover:text-white',
        )}
      >
        <Bell size={15} />
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold leading-none text-white">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-60 mt-2 w-[min(370px,calc(100vw-40px))] overflow-hidden rounded-2xl border border-border bg-card shadow-2xl">
          {/* Header: título + badge de no-leídas + cerrar (como el mockup). */}
          <div className="flex items-center gap-2.5 border-b border-border px-4 py-3">
            <span className="text-sm font-semibold text-fg">Notificaciones</span>
            {unread > 0 && (
              <span className="rounded-full bg-brand-soft px-2 py-0.5 text-[11.5px] font-semibold text-brand">
                {unread} sin leer
              </span>
            )}
            <button
              onClick={() => setOpen(false)}
              aria-label="Cerrar notificaciones"
              className="ml-auto text-muted hover:text-fg-2"
            >
              <X size={16} />
            </button>
          </div>

          {items.length === 0 ? (
            <div className="px-4 py-6 text-center text-xs text-muted">
              No tienes notificaciones.
            </div>
          ) : (
            <ul className="max-h-[60vh] divide-y divide-border overflow-auto">
              {items.map((n) => (
                <li key={n.id}>
                  <button
                    onClick={() => onItem(n)}
                    className={clsx(
                      'block w-full px-4 py-3 text-left transition hover:bg-card-2',
                      n.leida && 'opacity-60',
                    )}
                  >
                    <div className="flex items-start gap-2.5">
                      <span
                        className={clsx(
                          'mt-1.5 h-2 w-2 shrink-0 rounded-full',
                          n.leida ? 'bg-transparent' : 'bg-brand',
                        )}
                      />
                      <div className="min-w-0">
                        <p className="text-xs font-semibold text-fg">{n.titulo}</p>
                        <p className="mt-0.5 text-xs text-fg-2 text-pretty">{n.mensaje}</p>
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* Footer: marcar todas (el mockup también trae "Ver todas" → página,
              diferida por decisión del PO: notificaciones = burbuja, no página). */}
          {unread > 0 && (
            <div className="flex items-center border-t border-border bg-card-2 px-4 py-2.5">
              <button
                onClick={() => marcarTodas()}
                className="text-xs font-semibold text-fg-2 hover:text-fg"
              >
                Marcar todas como leídas
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
